import React, { useState, useEffect } from "react";
import { apiUrl, getApiUrl } from "../../../config/config";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const Toast = Swal.mixin({
	toast: true,
	position: "top-end",
	showConfirmButton: false,
	timer: 3000,
	timerProgressBar: true,
	didOpen: (toast) => {
		toast.onmouseenter = Swal.stopTimer;
		toast.onmouseleave = Swal.resumeTimer;
	},
});

function Appointments() {
	const navigate = useNavigate();
	const [appointments, setAppointments] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [currentDate, setCurrentDate] = useState(new Date());
	const [view, setView] = useState("month");

	useEffect(() => {
		fetchAppointments();
	}, []);

	const fetchAppointments = async () => {
		setIsLoading(true);
		let url = apiUrl;
		const ibAppUrl = await getApiUrl();
		if (ibAppUrl) {
			url = ibAppUrl;
		}

		try {
			const response = await fetch(url + "/getList", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					table: "appointments",
				}),
			});

			if (response.ok) {
				let data = await response.json();
				if (ibAppUrl) {
					data.data = data;
				}
				console.log("APPOINTMENTS:", data);
				setAppointments(data.data || []);
			} else {
				Toast.fire({
					icon: "error",
					title: "Error al cargar las citas",
					background: "#1e2836",
				});
			}
		} catch (error) {
			console.error("Error fetching appointments:", error);
			Toast.fire({
				icon: "error",
				title: "Error al cargar las citas",
				background: "#1e2836",
			});
		}
		setIsLoading(false);
	};

	const handleEditAppointment = (appointmentData) => {
		// Convertir datos a formato traducido para mantener consistencia
		const translatedData = {
			"ID Cita": appointmentData.idCita,
			"Nombre Cliente": appointmentData.nombreCliente,
			"Teléfono Cliente": appointmentData.telefonoCliente,
			"Email Cliente": appointmentData.emailCliente,
			"Fecha Hora Cita": appointmentData.fechaHoraCita,
			"Nombre Vehículo": appointmentData.nombreVehiculo,
			"Marca Vehículo": appointmentData.marcaVehiculo,
			"Modelo Vehículo": appointmentData.modeloVehiculo,
			"Año Vehículo": appointmentData.anoVehiculo,
			"Nombre Vendedor": appointmentData.nombreVendedor,
			"ID Sucursal": appointmentData.idSucursal,
			Estado: appointmentData.estado,
			"Fecha Creación": appointmentData.fechaCreacion,
		};

		navigate("/appointmentsDetails", {
			state: { type: "edit", data: translatedData },
		});
	};

	// Funciones para navegación del calendario
	const getDaysInMonth = (date) => {
		const year = date.getFullYear();
		const month = date.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const daysInMonth = lastDay.getDate();
		const startDay = firstDay.getDay();

		const days = [];

		// Días del mes anterior
		for (let i = startDay - 1; i >= 0; i--) {
			const prevDate = new Date(year, month, -i);
			days.push({ date: prevDate, isCurrentMonth: false });
		}

		// Días del mes actual
		for (let day = 1; day <= daysInMonth; day++) {
			const currentDate = new Date(year, month, day);
			days.push({ date: currentDate, isCurrentMonth: true });
		}

		// Días del mes siguiente para completar la grilla
		const remainingDays = 42 - days.length;
		for (let day = 1; day <= remainingDays; day++) {
			const nextDate = new Date(year, month + 1, day);
			days.push({ date: nextDate, isCurrentMonth: false });
		}

		return days;
	};

	const getAppointmentsForDate = (date) => {
		return appointments.filter((appointment) => {
			const appointmentDate = new Date(appointment.fechaHoraCita);
			return appointmentDate.toDateString() === date.toDateString();
		});
	};

	const navigateMonth = (direction) => {
		const newDate = new Date(currentDate);
		newDate.setMonth(currentDate.getMonth() + direction);
		setCurrentDate(newDate);
	};

	const getStatusColor = (estado) => {
		switch (estado) {
			case "Confirmada":
				return "success";
			case "Cancelada":
				return "danger";
			case "Realizada":
				return "secondary";
			default:
				return "primary";
		}
	};

	const formatTime = (dateTime) => {
		const date = new Date(dateTime);
		return date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
	};

	const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

	const dayNames = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

	if (isLoading) {
		return (
			<div className="text-center">
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Cargando...</span>
				</div>
			</div>
		);
	}

	const calendarDays = getDaysInMonth(currentDate);

	return (
		<div>
			<div className="d-flex justify-content-between align-items-center mb-4">
				<h1>Calendario de Citas</h1>
				<button className="btn btn-outline-primary" onClick={() => navigate("/appointmentsDetails", { state: { type: "add" } })}>
					<i className="fa fa-plus"></i> Nueva Cita
				</button>
			</div>

			{/* Navegación del calendario */}
			<div className="card">
				<div className="card-header d-flex justify-content-between align-items-center">
					<button className="btn btn-outline-secondary" onClick={() => navigateMonth(-1)}>
						<i className="fa fa-chevron-left"></i>
					</button>
					<h4 className="mb-0">
						{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
					</h4>
					<button className="btn btn-outline-secondary" onClick={() => navigateMonth(1)}>
						<i className="fa fa-chevron-right"></i>
					</button>
				</div>

				<div className="card-body p-0">
					{/* Encabezados de días */}
					<div className="row g-0">
						{dayNames.map((day, index) => (
							<div key={index} className="col text-center p-2 calendar-header fw-bold">
								{day}
							</div>
						))}
					</div>

					{/* Grilla del calendario */}
					<div className="calendar-grid">
						{Array.from({ length: 6 }, (_, weekIndex) => (
							<div key={weekIndex} className="row g-0">
								{calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7).map((dayObj, dayIndex) => {
									const dayAppointments = getAppointmentsForDate(dayObj.date);
									const isToday = dayObj.date.toDateString() === new Date().toDateString();

									return (
										<div
											key={dayIndex}
											className={`col border-end border-bottom calendar-day ${!dayObj.isCurrentMonth ? "other-month" : ""} ${
												isToday ? "today" : ""
											}`}
											style={{ minHeight: "160px", cursor: "pointer" }}
											onClick={() =>
												navigate("/appointmentsDetails", {
													state: { type: "add", selectedDate: dayObj.date },
												})
											}>
											<div className="p-2">
												<div className={`day-number ${isToday ? "fw-bold text-warning" : ""}`}>{dayObj.date.getDate()}</div>
												<div className="appointments-list">
													{dayAppointments.slice(0, 4).map((appointment, index) => (
														<div
															key={index}
															className={`appointment-item badge bg-${getStatusColor(
																appointment.estado
															)} text-truncate d-block mb-1`}
															style={{ fontSize: "0.65rem", cursor: "pointer", maxWidth: "100%" }}
															onClick={(e) => {
																e.stopPropagation();
																handleEditAppointment(appointment);
															}}
															title={`${formatTime(appointment.fechaHoraCita)} - ${appointment.nombreVendedor} - ${
																appointment.nombreVehiculo
															}`}>
															{formatTime(appointment.fechaHoraCita)} {appointment.nombreVendedor}
														</div>
													))}
													{dayAppointments.length > 4 && (
														<div
															className="badge bg-info text-dark"
															style={{ fontSize: "0.6rem", cursor: "pointer" }}
															onClick={(e) => {
																e.stopPropagation();
																// Mostrar todas las citas del día
																Swal.fire({
																	title: `Citas del ${dayObj.date.toLocaleDateString("es-ES")}`,
																	html: dayAppointments
																		.map(
																			(app) =>
																				`<div class="text-start mb-2">
																			<strong>${formatTime(app.fechaHoraCita)}</strong> - ${app.nombreVendedor}<br>
																			<small>${app.nombreVehiculo} - Estado: ${app.estado}</small>
																		</div>`
																		)
																		.join(""),
																	width: 600,
																	background: "#1e2836",
																	color: "#fff",
																});
															}}>
															+{dayAppointments.length - 4} más
														</div>
													)}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						))}
					</div>
				</div>
			</div>

			<div className="mt-4">
				<h5>Leyenda de Estados:</h5>
				<div className="d-flex gap-3">
					<span className="badge bg-success">Confirmada</span>
					<span className="badge bg-danger">Cancelada</span>
					<span className="badge bg-secondary">Realizada</span>
				</div>
			</div>

			<style jsx>{`
				.calendar-header {
					background-color: #2a3f5f !important;
					color: #ffffff !important;
					border-bottom: 1px solid #495057 !important;
				}

				.calendar-day {
					background-color: rgba(30, 40, 54, 0.4);
					color: #ffffff;
					border-color: #495057 !important;
				}

				.calendar-day:hover {
					background-color: rgba(42, 63, 95, 0.3) !important;
				}

				.other-month {
					background-color: rgba(42, 63, 95, 0.3) !important;
					color: #6c757d !important;
				}

				.other-month:hover {
					background-color: rgba(26, 35, 50, 0.3) !important;
				}

				.today {
					background-color: rgba(42, 63, 95, 0.6) !important;
					border: 2px solid #fbbf24 !important;
				}

				.today .day-number {
					color: #fbbf24 !important;
				}

				.appointment-item:hover {
					transform: scale(1.02);
					box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
				}

				.day-number {
					font-size: 0.9rem;
					margin-bottom: 0.5rem;
				}

				.appointments-list {
					max-height: 110px;
					overflow-y: auto;
				}

				.card {
					background-color: transparent !important;
					border: 1px solid #495057 !important;
				}

				.card-header {
					background-color: rgba(42, 63, 95, 0.8) !important;
					border-bottom: 1px solid #495057 !important;
				}
			`}</style>
		</div>
	);
}

export default Appointments;
